import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import sanitizeHtml from 'sanitize-html';
import { useTheme } from '../../contexts/ThemeContext';
import CalloutBlock from './CalloutBlock';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { CheckIcon, ClipboardIcon, LockClosedIcon as LockIcon } from '@heroicons/react/24/outline';

const CodeBlock = ({ language, value, isDark }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`group relative my-8 rounded-2xl overflow-hidden shadow-2xl border ${isDark ? 'border-white/5' : 'border-black/5'}`}>
      <div className={`flex items-center justify-between px-6 py-3 border-b ${isDark ? 'bg-gray-900/80 border-white/5' : 'bg-gray-100/80 border-black/5'} backdrop-blur-md`}>
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/50" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
            <div className="w-3 h-3 rounded-full bg-green-500/50" />
          </div>
          <span className={`ml-4 text-xs font-black uppercase tracking-widest ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {language || 'code'}
          </span>
        </div>
        <button
          className={`
            hidden group-hover:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-300
            ${isDark ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-black/5 text-gray-600 hover:text-black'}
          `}
        >
          <><LockIcon size={14} /> Protected Content</>
        </button>
      </div>
      <div className="overflow-x-auto custom-scrollbar">
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={language}
          PreTag="div"
          customStyle={{
            margin: 0,
            borderRadius: 0,
            padding: '2rem 1.5rem',
            fontSize: '0.95rem',
            lineHeight: '1.7',
            background: isDark ? '#0a0a0a' : '#1e1e1e',
            minWidth: '100%'
          }}
          showLineNumbers={true}
          lineNumberStyle={{ minWidth: '3em', paddingRight: '1em', color: '#4b5563', textAlign: 'right' }}
        >
          {value}
        </SyntaxHighlighter>
      </div>
    </div>
  );
};

/**
 * Elite Badge Component
 * Displays badges for Production Ready, Interview Critical, etc.
 */
const EliteBadge = ({ icon, text, color }) => {
  return (
    <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-black tracking-widest uppercase border ${color}`}>
      <span>{icon}</span>
      <span>{text}</span>
    </span>
  );
};

/**
 * Elite Markdown Renderer
 * Enhanced markdown renderer with custom components for elite content
 */
const EliteMarkdownRenderer = ({ content, onHeadingsCollected }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  useEffect(() => {
    if (onHeadingsCollected && content) {
      const headingLines = content.split('\n').filter(line => /^#{2,3}\s/.test(line));
      const headings = headingLines.map((line, index) => {
        const level = line.startsWith('###') ? 3 : 2;
        const text = line.replace(/^#{2,3}\s+/, '').trim();
        const id = text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
        return { id, text, level };
      });
      onHeadingsCollected(headings);
    }
  }, [content, onHeadingsCollected]);

  // Parse elite content markers from markdown
  const parseEliteContent = (text) => {
    // This function can be extended to parse custom syntax
    // For now, we'll rely on standard markdown with special markers
    return text;
  };

  // Sanitize content before rendering if it contains HTML
  const sanitizedContent = sanitizeHtml(content, {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat([
      'div', 'span', 'img', 'iframe', 'svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'ellipse'
    ]),
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      '*': ['className', 'style', 'id', 'width', 'height', 'viewBox', 'fill', 'stroke', 'd', 'cx', 'cy', 'r', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'points', 'rx', 'ry']
    }
  });

  return (
    <div className={`
      ${isDark ? 'text-gray-100' : 'text-gray-900'} 
      break-words overflow-wrap-anywhere 
      prose prose-lg max-w-none
      ${isDark ? 'prose-invert' : ''}
    `}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          // Images
          img: ({ src, alt }) => (
            <div className="my-10 flex flex-col items-center">
              <img 
                src={src} 
                alt={alt} 
                className="rounded-3xl shadow-2xl border border-primary/10 max-w-full h-auto" 
              />
              {alt && <p className="mt-4 text-xs font-bold text-gray-500 uppercase tracking-widest">{alt}</p>}
            </div>
          ),

          // Headings with enhanced styling
          h1: ({ children }) => {
            const text = String(children);
            const id = text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
            return (
              <div className="mb-12 relative" id={id}>
                <h1
                  className={`
                    text-5xl
                    sm:text-6xl
                    font-black
                    mb-8
                    tracking-tight
                    ${isDark ? 'text-white' : 'text-slate-900'}
                    leading-[1]
                  `}
                >
                  {children}
                </h1>
                <div className="flex flex-wrap gap-4 mt-8">
                  <EliteBadge icon="🏆" text="Production Level" color="bg-emerald-500/5 text-emerald-500 border-emerald-500/10" />
                  <EliteBadge icon="⚡" text="Interview Target" color="bg-amber-500/5 text-amber-500 border-amber-500/10" />
                </div>
              </div>
            );
          },

          h2: ({ children }) => {
            const title = String(children);
            const id = title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
            const lowerTitle = title.toLowerCase();
            const isMentalModel = lowerTitle.includes('mental model') || lowerTitle.includes('visual intuition');
            const isArchitecture = lowerTitle.includes('architecture');
            const isEnterprise = lowerTitle.includes('enterprise');

            if (isMentalModel) {
              return <CalloutBlock type="mental_model" title={title}>{null}</CalloutBlock>;
            }
            if (isArchitecture) {
              return <CalloutBlock type="performance" title={title}>{null}</CalloutBlock>;
            }
            if (isEnterprise) {
              return <CalloutBlock type="enterprise" title={title}>{null}</CalloutBlock>;
            }

            return (
              <h2
                id={id}
                className={`
                  text-4xl
                  font-black
                  mt-20
                  mb-8
                  flex
                  items-center
                  gap-5
                  tracking-tight
                  ${isDark ? 'text-white' : 'text-slate-900'}
                `}
              >
                <div className="w-2 h-10 bg-primary rounded-full shadow-sm shadow-primary/20" />
                {children}
              </h2>
            );
          },

          h3: ({ children }) => {
            const title = String(children);
            const id = title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
            const lowerTitle = title.toLowerCase();

            // Interview Questions sub-categories (Fundamental, Scenario-Based, Debugging, Architecture)
            const isInterviewCategory =
              lowerTitle.includes('fundamental questions') ||
              lowerTitle.includes('scenario-based questions') ||
              lowerTitle.includes('scenario based questions') ||
              lowerTitle.includes('debugging questions') ||
              lowerTitle.includes('architecture questions');

            const isMentalModel = lowerTitle.includes('mental model') || lowerTitle.includes('visual intuition') || lowerTitle.includes('diagram');
            const isEnterprise = lowerTitle.includes('enterprise');
            const isPerspective = lowerTitle.includes('perspective');

            // Special styling for Interview Questions categories
            if (isInterviewCategory) {
              let icon = '📝';
              let color = 'from-primary/10 to-secondary/10 border-primary/20';

              if (lowerTitle.includes('fundamental')) {
                icon = '📚';
                color = 'from-primary/10 to-secondary/10 border-primary/20';
              } else if (lowerTitle.includes('scenario')) {
                icon = '🎯';
                color = 'from-purple-500/10 to-pink-500/10 border-purple-500/20';
              } else if (lowerTitle.includes('debugging')) {
                icon = '🐛';
                color = 'from-red-500/10 to-orange-500/10 border-red-500/20';
              } else if (lowerTitle.includes('architecture')) {
                icon = '🏗️';
                color = 'from-green-500/10 to-emerald-500/10 border-green-500/20';
              }

              return (
                <div
                  id={id}
                  className={`
                    mt-10 mb-6 p-6 rounded-2xl border-2
                    bg-gradient-to-r ${color}
                    backdrop-blur-sm
                  `}
                >
                  <h3 className={`
                    text-2xl font-black tracking-tight
                    ${isDark ? 'text-white' : 'text-slate-900'}
                    flex items-center gap-3
                  `}>
                    <span className="text-3xl">{icon}</span>
                    {children}
                  </h3>
                </div>
              );
            }

            if (isMentalModel) {
              return (
                <div className="my-12">
                  <CalloutBlock type="mental_model" title={title}>
                    <div className="mt-1" />
                  </CalloutBlock>
                </div>
              );
            }

            if (isEnterprise) {
              return (
                <div className="my-12">
                  <CalloutBlock type="enterprise" title={title}>
                    <div className="mt-1" />
                  </CalloutBlock>
                </div>
              );
            }

            if (isPerspective) {
              return (
                <div className="my-12">
                  <CalloutBlock type="interview" title={title}>
                    <div className="mt-1" />
                  </CalloutBlock>
                </div>
              );
            }

            return (
              <h3
                id={id}
                className={`
                  text-2xl
                  font-black
                  mt-14
                  mb-6
                  tracking-tight
                  ${isDark ? 'text-gray-100' : 'text-slate-800'}
                  flex items-center gap-4
                `}
              >
                <div className="w-2 h-7 bg-secondary rounded-full shadow-sm shadow-secondary/20" />
                {children}
              </h3>
            );
          },

          h4: ({ children }) => (
            <h4
              className={`
                text-xl
                font-semibold
                mt-6
                mb-3
                ${isDark ? 'text-gray-200' : 'text-gray-700'}
              `}
            >
              {children}
            </h4>
          ),

          // Paragraphs
          p: ({ children }) => (
            <p className={`mb-8 text-lg leading-[1.8] font-medium tracking-tight break-words ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {children}
            </p>
          ),

          // Code blocks with syntax highlighting
          code({ inline, className, children }) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : 'java';

            if (inline) {
              return (
                <code
                  className={`
                    px-2
                    py-0.5
                    rounded-md
                    text-[0.9em]
                    font-black
                    font-mono
                    ${isDark ? 'bg-white/5 text-primary shadow-sm ring-1 ring-white/10' : 'bg-primary/5 text-primary shadow-sm ring-1 ring-primary/10'}
                  `}
                >
                  {children}
                </code>
              );
            }

            return (
              <CodeBlock language={language} value={String(children).replace(/\n$/, '')} isDark={isDark} />
            );
          },

          // Lists
          ul: ({ children }) => (
            <ul className={`list-disc pl-6 mb-8 space-y-3 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              {children}
            </ul>
          ),

          ol: ({ children }) => (
            <ol className={`list-decimal pl-6 mb-8 space-y-3 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              {children}
            </ol>
          ),

          li: ({ children }) => (
            <li className="text-lg leading-relaxed break-words">
              {children}
            </li>
          ),

          // Blockquotes - can be used for callouts
          blockquote: ({ children }) => {
            // Find text content even if nested in paragraphs
            const extractText = (node) => {
              if (typeof node === 'string') return node;
              if (Array.isArray(node)) return node.map(extractText).join(' ');
              if (node.props && node.props.children) return extractText(node.props.children);
              return '';
            };

            const text = extractText(children).toUpperCase();

            // Detect callout type from content
            if (text.includes('⚠️') || text.includes('WARNING')) {
              return <CalloutBlock type="warning" title="Warning">{children}</CalloutBlock>;
            }
            if (text.includes('💡') || text.includes('TIP')) {
              return <CalloutBlock type="tip" title="Pro Tip">{children}</CalloutBlock>;
            }
            if (text.includes('🏢') || text.includes('ENTERPRISE') || text.includes('USE CASE')) {
              return <CalloutBlock type="enterprise" title="Enterprise Insight">{children}</CalloutBlock>;
            }
            if (text.includes('🎤') || text.includes('INTERVIEW')) {
              return <CalloutBlock type="interview" title="Interview Focus">{children}</CalloutBlock>;
            }
            if (text.includes('💥') || text.includes('FAILURE') || text.includes('STORY')) {
              return <CalloutBlock type="production_story" title="Production Story">{children}</CalloutBlock>;
            }

            // Default blockquote
            return (
              <blockquote
                className={`
                  border-l-4
                  border-primary
                  pl-4
                  my-6
                  italic
                  ${isDark ? 'text-gray-300' : 'text-gray-600'}
                `}
              >
                {children}
              </blockquote>
            );
          },

          // Tables (for decision matrices)
          table: ({ children }) => (
            <div className="my-10 overflow-x-auto rounded-2xl shadow-2xl border border-primary/20">
              <table className={`min-w-full divide-y ${isDark ? 'bg-gray-950 divide-gray-800' : 'bg-white divide-gray-200'}`}>
                {children}
              </table>
            </div>
          ),

          thead: ({ children }) => (
            <thead className={`${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
              {children}
            </thead>
          ),

          tbody: ({ children }) => (
            <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-200'}`}>
              {children}
            </tbody>
          ),

          th: ({ children }) => (
            <th className={`px-6 py-4 text-left text-xs font-black uppercase tracking-widest ${isDark ? 'text-primary' : 'text-secondary'}`}>
              {children}
            </th>
          ),

          td: ({ children }) => (
            <td className={`px-6 py-5 text-base font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {children}
            </td>
          ),

          tr: ({ children }) => (
            <tr className={`hover:${isDark ? 'bg-gray-800' : 'bg-gray-50'} transition-colors`}>
              {children}
            </tr>
          ),

          // Links
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={`
                underline
                font-semibold
                transition-colors
                ${isDark ? 'text-primary hover:text-secondary' : 'text-primary hover:text-secondary'}
              `}
            >
              {children}
            </a>
          ),

          // Strong/Bold
          strong: ({ children }) => (
            <strong className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {children}
            </strong>
          ),

          // Emphasis/Italic
          em: ({ children }) => (
            <em className={`italic ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {children}
            </em>
          ),

          // Horizontal Rule
          hr: () => (
            <hr className={`my-8 border-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`} />
          ),
        }}
      >
        {parseEliteContent(content)}
      </ReactMarkdown>
    </div>
  );
};

export default EliteMarkdownRenderer;
